import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from "../ui/command";
import {
  Search, FileText, ArrowRight, X, ArrowLeft,
  Home, Briefcase, Package, FolderOpen, Sparkles,
  BookOpen, Phone, Globe, History, Info, ShoppingBag,
  Building2, Pencil, Eye, RotateCcw, Undo2, Redo2, Activity,
  LayoutTemplate, Crown, Settings, Keyboard,
  Image as ImageIcon,
  ClipboardList,
} from "lucide-react";
import type { TemplateMeta } from "../../utils/templateApi";
import { useTemplateSafe } from "./TemplateContext";
import { getAccent, getInitials } from "../dashboard/dashboardConstants";
import { navigation as brandingNav } from "../../siteConfig";
import { navigation as storeNav } from "../../storeSiteConfig";

/* ========================================================
   Command Palette (Ctrl+K)

   Global search overlay for quick navigation across
   templates, pages, and settings.

   Two modes:
   1. Dashboard mode — search templates, navigate to them
   2. Template mode  — search pages within the template
   ======================================================== */

/* Page icon mapping */
const PAGE_ICONS: Record<string, typeof Home> = {
  "/": Home,
  "/about": Info,
  "/services": Briefcase,
  "/service-a": Briefcase,
  "/service-b": Briefcase,
  "/products": Package,
  "/product-a": Package,
  "/product-b": Package,
  "/projects": FolderOpen,
  "/consulting": Sparkles,
  "/blog": BookOpen,
  "/contact": Phone,
  "/catalog": ShoppingBag,
  "/seo-manager": Globe,
  "/change-history": History,
  "/activity-log": Activity,
  "/business-info": Building2,
  "/qa-checklist": ClipboardList,
  "/media-library": ImageIcon,
  "/media": ImageIcon,
};

function getPageIcon(path: string) {
  return PAGE_ICONS[path] || FileText;
}

/* Flatten navigation items */
interface FlatNavItem {
  label: string;
  path: string;
}

function flattenNav(nav: { label: string; path: string; children?: { label: string; path: string }[] }[]): FlatNavItem[] {
  const items: FlatNavItem[] = [];
  for (const item of nav) {
    items.push({ label: item.label, path: item.path });
    if (item.children) {
      for (const child of item.children) {
        items.push({ label: child.label, path: child.path });
      }
    }
  }
  return items;
}

/* Settings pages */
const SETTINGS_PAGES: FlatNavItem[] = [
  { label: "\u05E0\u05D9\u05D4\u05D5\u05DC SEO", path: "/seo-manager" },
  { label: "\u05D4\u05D9\u05E1\u05D8\u05D5\u05E8\u05D9\u05D9\u05EA \u05E9\u05D9\u05E0\u05D5\u05D9\u05D9\u05DD", path: "/change-history" },
  { label: "\u05D9\u05D5\u05DE\u05DF \u05E4\u05E2\u05D9\u05DC\u05D5\u05EA", path: "/activity-log" },
  { label: "\u05DE\u05D9\u05D3\u05E2 \u05E2\u05E1\u05E7\u05D9", path: "/business-info" },
  { label: "\u05E6\u05F3\u05E7\u05DC\u05D9\u05E1\u05D8 QA", path: "/qa-checklist" },
  { label: "\u05E1\u05E4\u05E8\u05D9\u05D9\u05EA \u05DE\u05D3\u05D9\u05D4 \u05DB\u05DC\u05DC\u05D9\u05EA", path: "/media-library" },
  { label: "\u05DE\u05D3\u05D9\u05D4 \u05E4\u05E8\u05D5\u05D9\u05E7\u05D8", path: "/media" },
];

/* ========================================================
   Props
   ======================================================== */

interface CommandPaletteProps {
  templates?: TemplateMeta[];
  dashboardMode?: boolean;
}

/* ========================================================
   Component
   ======================================================== */

export function CommandPalette({ templates = [], dashboardMode = false }: CommandPaletteProps) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const tplCtx = useTemplateSafe();

  /* Keyboard shortcut: Ctrl+K */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  /* Build page list for template mode */
  const templatePages = useMemo(() => {
    if (!tplCtx) return [];
    const nav = tplCtx.templateType === "store" ? storeNav : brandingNav;
    return flattenNav(nav);
  }, [tplCtx]);

  const goToTemplate = useCallback((id: string) => {
    setOpen(false);
    navigate(`/t/${id}/`);
  }, [navigate]);

  const goToPage = useCallback((path: string) => {
    setOpen(false);
    if (tplCtx) {
      navigate(tplCtx.sitePath(path));
    }
  }, [navigate, tplCtx]);

  const goToDashboard = useCallback(() => {
    setOpen(false);
    navigate("/");
  }, [navigate]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center pt-[15vh]"
      onClick={() => setOpen(false)}
      dir="rtl"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Command box */}
      <div
        className="relative w-full max-w-lg mx-4"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: "cmdFadeIn 150ms ease-out" }}
      >
        <style>{`
          @keyframes cmdFadeIn {
            from { opacity: 0; transform: translateY(-8px) scale(0.98); }
            to   { opacity: 1; transform: translateY(0) scale(1); }
          }
        `}</style>

        <Command
          className="bg-white rounded-xl shadow-2xl border border-gray-200/80 overflow-hidden"
          filter={(value, search) => {
            if (!search) return 1;
            const s = search.toLowerCase();
            return value.toLowerCase().includes(s) ? 1 : 0;
          }}
        >
          {/* Search input */}
          <CommandInput
            placeholder={dashboardMode ? "\u05D7\u05D9\u05E4\u05D5\u05E9 \u05EA\u05D1\u05E0\u05D9\u05D5\u05EA, \u05D3\u05E4\u05D9\u05DD, \u05D4\u05D2\u05D3\u05E8\u05D5\u05EA..." : "\u05D7\u05D9\u05E4\u05D5\u05E9 \u05D3\u05E4\u05D9\u05DD, \u05D4\u05D2\u05D3\u05E8\u05D5\u05EA..."}
            autoFocus
            className="h-12 text-sm"
          />

          <CommandList className="max-h-[340px] overflow-y-auto">
            <CommandEmpty className="py-8 text-center">
              <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Search className="w-6 h-6 text-gray-300" />
              </div>
              <p className="text-sm text-gray-400">{"\u05DC\u05D0 \u05E0\u05DE\u05E6\u05D0\u05D5 \u05EA\u05D5\u05E6\u05D0\u05D5\u05EA"}</p>
            </CommandEmpty>

            {/* Dashboard mode: Templates */}
            {dashboardMode && templates.length > 0 && (
              <CommandGroup heading={"\u05EA\u05D1\u05E0\u05D9\u05D5\u05EA"}>
                {templates.map((t) => {
                  const accent = getAccent(t.id);
                  const initials = getInitials(t.clientName);
                  const isStore = (t.templateType || "branding") === "store";
                  return (
                    <CommandItem
                      key={t.id}
                      value={`${t.clientName} ${t.clientNumber} ${(t.tags || []).join(" ")} ${t.characterizer} ${t.marketer} ${t.designer} ${t.developer}`}
                      onSelect={() => goToTemplate(t.id)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer"
                    >
                      <div
                        className={`w-8 h-8 rounded-lg bg-gradient-to-br ${accent.bg} flex items-center justify-center text-white text-xs shrink-0`}
                        style={{ fontWeight: 700 }}
                      >
                        {initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-800 truncate" style={{ fontWeight: 600 }}>
                            {t.clientName}
                          </span>
                          {t.isMaster && <Crown className="w-3 h-3 text-amber-500 shrink-0" />}
                        </div>
                        {t.clientNumber && (
                          <span className="text-[11px] text-gray-400 truncate block">{t.clientNumber}</span>
                        )}
                      </div>
                      {isStore ? (
                        <ShoppingBag className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                      ) : (
                        <LayoutTemplate className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}

            {/* Template mode: Pages */}
            {!dashboardMode && tplCtx && templatePages.length > 0 && (
              <>
                <CommandGroup heading={"\u05D3\u05E4\u05D9 \u05D4\u05D0\u05EA\u05E8"}>
                  {templatePages.map((page) => {
                    const Icon = getPageIcon(page.path);
                    return (
                      <CommandItem
                        key={page.path}
                        value={`${page.label} ${page.path}`}
                        onSelect={() => goToPage(page.path)}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer"
                      >
                        <div className="w-7 h-7 rounded-md bg-zap-pale flex items-center justify-center shrink-0">
                          <Icon className="w-3.5 h-3.5 text-zap" />
                        </div>
                        <span className="text-sm text-gray-700" style={{ fontWeight: 500 }}>
                          {page.label}
                        </span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>

                <CommandSeparator className="my-1" />

                <CommandGroup heading={"\u05D4\u05D2\u05D3\u05E8\u05D5\u05EA"}>
                  {SETTINGS_PAGES.map((page) => {
                    const Icon = getPageIcon(page.path);
                    return (
                      <CommandItem
                        key={page.path}
                        value={`${page.label} ${page.path} settings`}
                        onSelect={() => goToPage(page.path)}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer"
                      >
                        <div className="w-7 h-7 rounded-md bg-gray-100 flex items-center justify-center shrink-0">
                          <Icon className="w-3.5 h-3.5 text-gray-500" />
                        </div>
                        <span className="text-sm text-gray-700" style={{ fontWeight: 500 }}>
                          {page.label}
                        </span>
                        <Settings className="w-3 h-3 text-gray-300 mr-auto" />
                      </CommandItem>
                    );
                  })}
                </CommandGroup>

                <CommandSeparator className="my-1" />
              </>
            )}

            {/* Quick actions */}
            <CommandGroup heading={"\u05E4\u05E2\u05D5\u05DC\u05D5\u05EA \u05DE\u05D4\u05D9\u05E8\u05D5\u05EA"}>
              {!dashboardMode && (
                <CommandItem
                  value={"\u05D7\u05D6\u05E8\u05D4 \u05DC\u05D3\u05E9\u05D1\u05D5\u05E8\u05D3 \u05DB\u05DC \u05D4\u05EA\u05D1\u05E0\u05D9\u05D5\u05EA dashboard"}
                  onSelect={goToDashboard}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer"
                >
                  <div className="w-7 h-7 rounded-md bg-zap/10 flex items-center justify-center shrink-0">
                    <ArrowLeft className="w-3.5 h-3.5 text-zap" />
                  </div>
                  <span className="text-sm text-gray-700" style={{ fontWeight: 500 }}>
                    {"\u05D7\u05D6\u05E8\u05D4 \u05DC\u05D3\u05E9\u05D1\u05D5\u05E8\u05D3"}
                  </span>
                </CommandItem>
              )}
              <CommandItem
                value={"\u05D8\u05E4\u05E1\u05D9 \u05D0\u05D9\u05E1\u05D5\u05E3 \u05D7\u05D5\u05DE\u05E8\u05D9\u05DD \u05DC\u05E7\u05D5\u05D7\u05D5\u05EA intake forms"}
                onSelect={() => { setOpen(false); navigate("/intake-forms"); }}
                className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer"
              >
                <div className="w-7 h-7 rounded-md bg-blue-50 flex items-center justify-center shrink-0">
                  <FileText className="w-3.5 h-3.5 text-blue-500" />
                </div>
                <span className="text-sm text-gray-700" style={{ fontWeight: 500 }}>
                  {"\u05D8\u05E4\u05E1\u05D9 \u05D0\u05D9\u05E1\u05D5\u05E3 \u05D7\u05D5\u05DE\u05E8\u05D9\u05DD"}
                </span>
              </CommandItem>
              <CommandItem
                value={"\u05E7\u05D9\u05E6\u05D5\u05E8\u05D9 \u05DE\u05E7\u05E9\u05D9\u05DD keyboard shortcuts"}
                className="flex items-center gap-3 px-3 py-2 rounded-lg"
                disabled
              >
                <div className="w-7 h-7 rounded-md bg-gray-100 flex items-center justify-center shrink-0">
                  <Keyboard className="w-3.5 h-3.5 text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-gray-500" style={{ fontWeight: 500 }}>
                    {"\u05E7\u05D9\u05E6\u05D5\u05E8\u05D9 \u05DE\u05E7\u05E9\u05D9\u05DD"}
                  </span>
                  <div className="flex items-center gap-3 mt-0.5 text-[10px] text-gray-400">
                    <span>Ctrl+E {"\u2190"} {"\u05E2\u05E8\u05D9\u05DB\u05D4"}</span>
                    <span>Ctrl+Z {"\u2190"} {"\u05D1\u05D9\u05D8\u05D5\u05DC"}</span>
                    <span>Ctrl+Y {"\u2190"} {"\u05E9\u05D7\u05D6\u05D5\u05E8"}</span>
                    <span>Ctrl+K {"\u2190"} {"\u05D7\u05D9\u05E4\u05D5\u05E9"}</span>
                  </div>
                </div>
              </CommandItem>
            </CommandGroup>
          </CommandList>

          {/* Footer hint */}
          <div className="border-t border-gray-100 px-4 py-2 flex items-center justify-between text-[10px] text-gray-400">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-gray-100 rounded text-[9px] border border-gray-200" style={{ fontFamily: "monospace" }}>{"\u2191\u2193"}</kbd>
                {"\u05E0\u05D9\u05D5\u05D5\u05D8"}
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-gray-100 rounded text-[9px] border border-gray-200" style={{ fontFamily: "monospace" }}>{"\u21B5"}</kbd>
                {"\u05D1\u05D7\u05D9\u05E8\u05D4"}
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-gray-100 rounded text-[9px] border border-gray-200" style={{ fontFamily: "monospace" }}>esc</kbd>
                {"\u05E1\u05D2\u05D9\u05E8\u05D4"}
              </span>
            </div>
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 bg-gray-100 rounded text-[9px] border border-gray-200" style={{ fontFamily: "monospace" }}>Ctrl+K</kbd>
              {"\u05E4\u05EA\u05D9\u05D7\u05D4/\u05E1\u05D2\u05D9\u05E8\u05D4"}
            </span>
          </div>
        </Command>
      </div>
    </div>
  );
}

/**
 * SearchTriggerButton - shows in the dashboard header for opening the palette.
 */
export function SearchTriggerButton() {
  const handleClick = useCallback(() => {
    document.dispatchEvent(
      new KeyboardEvent("keydown", { key: "k", ctrlKey: true, bubbles: true })
    );
  }, []);

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") handleClick();
      }}
      className="flex items-center gap-2 px-3 py-1.5 bg-white/[0.08] hover:bg-white/[0.15] rounded-lg border border-white/[0.08] cursor-pointer"
    >
      <Search className="w-3.5 h-3.5 text-white/50" />
      <span className="text-xs text-white/40 hidden sm:inline">{"\u05D7\u05D9\u05E4\u05D5\u05E9..."}</span>
      <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-white/[0.06] text-white/30 text-[10px] rounded border border-white/[0.1]" style={{ fontFamily: "monospace" }}>
        Ctrl+K
      </kbd>
    </span>
  );
}